import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SiLinkedin, SiGithub, SiX } from "react-icons/si";

const WorkExperiencePage = ({ experience }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <section className="px-4 sm:px-6 md:px-10 lg:px-20 py-12 md:py-16">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto text-center mb-14">
        {/* Small Tag Title */}
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
          Work Experience
        </h2>

        <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-400 mt-3 max-w-2xl mx-auto">
          My work experiences across different companies and roles.
        </p>
        {/* Divider */}
        <div className="flex justify-center mt-6 mb-10">
          <div className="w-24 h-[3px] bg-gradient-to-r from-gray-400 to-gray-600 dark:from-gray-600 dark:to-gray-300 rounded-full"></div>
        </div>
        {/* Main Title */}
        <p
          className="text-sm sm:text-base font-semibold tracking-wide uppercase 
   text-gray-500 dark:text-gray-400 text-left"
        >
          All Experiences
        </p>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        {experience.map((exp, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
            viewport={{ once: true }}
            className="bg-gray-50 dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 sm:p-8"
          >
            {/* Header Row */}
            <button
              onClick={() => toggle(i)}
              className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-left"
            >
              <div className="flex items-start gap-4 w-full flex-wrap sm:flex-nowrap">
                {/* Logo */}
                <img
                  src={exp.logo}
                  alt={exp.company}
                  loading="lazy"
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl object-contain bg-gray-200 dark:bg-gray-700 p-2 border border-gray-300 dark:border-gray-600 flex-shrink-0"
                />

                <div className="min-w-0 flex-1">
                  {/* Company + Social Icons */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {exp.company}
                      </h3>

                      {/* Social Icons */}
                      <div className="flex items-center gap-2">
                        {exp.linkedin && (
                          <a
                            href={exp.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View LinkedIn"
                            className="relative group focus:outline-none"
                          >
                            <SiLinkedin
                              className="text-black dark:text-gray-300 hover:scale-110 transition"
                              size={17}
                            />
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 text-[10px] md:text-xs rounded-md bg-gray-900 text-white dark:bg-white dark:text-black opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
                              View LinkedIn
                            </span>
                          </a>
                        )}
                        {exp.github && (
                          <a
                            href={exp.github}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View GitHub"
                            className="relative group focus:outline-none"
                          >
                            <SiGithub
                              className="text-black dark:text-gray-300 hover:scale-110 transition"
                              size={17}
                            />
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 text-[10px] md:text-xs rounded-md bg-gray-900 text-white dark:bg-white dark:text-black opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
                              View GitHub
                            </span>
                          </a>
                        )}
                        {exp.twitter && (
                          <a
                            href={exp.twitter}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View Twitter / X"
                            className="relative group focus:outline-none"
                          >
                            <SiX
                              className="text-black dark:text-gray-300 hover:scale-110 transition"
                              size={17}
                            />
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 text-[10px] md:text-xs rounded-md bg-gray-900 text-white dark:bg-white dark:text-black opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
                              View Twitter
                            </span>
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Dropdown arrow */}
                    <div className="flex-shrink-0 mt-2 sm:mt-0">
                      {openIndex === i ? (
                        <ChevronUp
                          className="text-black-600 dark:text-white-400"
                          size={20}
                        />
                      ) : (
                        <ChevronDown
                          className="text-black-600 dark:text-white-400"
                          size={20}
                        />
                      )}
                    </div>
                  </div>

                  {/* Role */}
                  <p className="text-sm md:text-base mt-1 text-gray-500 dark:text-gray-400">
                    {exp.role}
                  </p>
                </div>

                {/* Date + Location */}
                <div className="flex-shrink-0 text-right flex flex-col items-end justify-center mt-2 sm:mt-0">
                  <p className="text-xs sm:text-sm md:text-sm text-gray-500 dark:text-gray-400">
                    {exp.date}
                  </p>
                  {exp.location && (
                    <p className="text-xs sm:text-sm md:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {exp.location}
                    </p>
                  )}
                </div>
              </div>
            </button>

            {/* Expandable Section */}
            <AnimatePresence>
              {openIndex === i && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35 }}
                  className="overflow-hidden mt-4"
                >
                  {/* Technologies & Tools */}
                  {exp.technologies && exp.technologies.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        Technologies & Tools
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {exp.technologies.map((tech, idx) => (
                          <a
                            key={idx}
                            href={tech.link || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm font-medium text-gray-900 dark:text-gray-100 shadow-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                          >
                            {tech.icon && (
                              <span className="flex items-center justify-center text-lg text-gray-700 dark:text-gray-200">
                                {tech.icon}
                              </span>
                            )}
                            <span>{tech.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Achievements / Contributions */}
                  <ul className="mt-2 space-y-2 text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed pl-5 list-disc">
                    {exp.points.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default WorkExperiencePage;
